import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { Flame, Sparkles, TrendingUp, Music, BookOpen, Dumbbell, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgressPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/progress/dna`)
      .then(r => setData(r.data))
      .catch(err => console.error('dna fetch', err))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] pt-20">
        <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 text-center">
        <p className="text-zinc-400">{t('progress.loginRequired')}</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-4 sm:px-6 md:px-12" data-testid="progress-page">
      <div className="max-w-7xl mx-auto">
        <Header t={t} totals={data.totals} streak={data.streak} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SkillsRadarCard data={data.skills_by_category} t={t} />
          <BpmLineCard data={data.bpm_progression} t={t} />
        </div>

        <div className="mb-6">
          <StreakHeatmapCard data={data.calendar_90d} t={t} />
        </div>

        <RepertoireCard data={data.repertoire} t={t} />
      </div>
    </div>
  );
};

const Header = ({ t, totals, streak }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-8"
  >
    <span className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-amber/10 border border-amber/30 text-amber text-xs font-bold uppercase tracking-widest">
      <Sparkles className="w-3 h-3" />
      {t('progress.tagline')}
    </span>
    <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">
      {t('progress.title')}
    </h1>
    <p className="text-zinc-400 max-w-2xl mb-6">{t('progress.subtitle')}</p>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Pill icon={Flame} label={t('progress.streakNow')} value={`${streak.current}`} accent="text-orange-400" />
      <Pill icon={TrendingUp} label={t('progress.streakLongest')} value={`${streak.longest}`} accent="text-amber" />
      <Pill icon={BookOpen} label={t('progress.lessonsDone')} value={totals.lessons_completed} accent="text-purple-300" />
      <Pill icon={Music} label={t('progress.songsPlayed')} value={totals.songs_completed} accent="text-cyan-300" />
    </div>
  </motion.div>
);

const Pill = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#121212] border border-white/5 rounded-2xl p-4">
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${accent}`} />
      <span className="text-zinc-500 text-xs uppercase tracking-widest">{label}</span>
    </div>
    <div className={`font-mono font-bold text-2xl ${accent}`}>{value}</div>
  </div>
);

const SkillsRadarCard = ({ data, t }) => {
  const chartData = (data || []).map(d => ({
    category: t(`skills.categories.${d.category}`),
    percent: d.percent,
  }));
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-white/5 rounded-2xl p-5 sm:p-6"
    >
      <h2 className="font-heading font-bold text-white mb-1">{t('progress.cards.radarTitle')}</h2>
      <p className="text-zinc-500 text-xs mb-4">{t('progress.cards.radarSubtitle')}</p>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData}>
            <PolarGrid stroke="#27272a" />
            <PolarAngleAxis dataKey="category" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#52525b', fontSize: 10 }} />
            <Radar dataKey="percent" stroke="#FF9F1C" fill="#FF9F1C" fillOpacity={0.35} />
            <Tooltip
              contentStyle={{ background: '#0E0E10', border: '1px solid #27272a', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: '#a1a1aa' }}
              formatter={(v) => [`${v}%`, t('progress.cards.radarTooltip')]}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

const BpmLineCard = ({ data, t }) => {
  const has = data && data.length > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="bg-[#121212] border border-white/5 rounded-2xl p-5 sm:p-6"
    >
      <h2 className="font-heading font-bold text-white mb-1">{t('progress.cards.bpmTitle')}</h2>
      <p className="text-zinc-500 text-xs mb-4">{t('progress.cards.bpmSubtitle')}</p>
      <div className="h-[260px] flex items-center justify-center">
        {has ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid stroke="#1f1f23" />
              <XAxis dataKey="date" tick={{ fill: '#52525b', fontSize: 10 }} />
              <YAxis tick={{ fill: '#52525b', fontSize: 10 }} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ background: '#0E0E10', border: '1px solid #27272a', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#a1a1aa' }}
              />
              <Line type="monotone" dataKey="max_bpm" stroke="#FF9F1C" strokeWidth={2.5} dot={false} name={t('progress.cards.bpmMax')} />
              <Line type="monotone" dataKey="avg_bpm" stroke="#22d3ee" strokeWidth={2} dot={false} name={t('progress.cards.bpmAvg')} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center text-zinc-500 text-sm">
            <Dumbbell className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            {t('progress.cards.bpmEmpty')}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const StreakHeatmapCard = ({ data, t }) => {
  const weeks = useMemo(() => {
    if (!data) return [];
    const cols = [];
    let week = [];
    let firstDay = new Date(data[0].date).getDay();
    firstDay = (firstDay + 6) % 7;
    for (let i = 0; i < firstDay; i++) week.push(null);
    for (const cell of data) {
      week.push(cell);
      if (week.length === 7) {
        cols.push(week);
        week = [];
      }
    }
    if (week.length) {
      while (week.length < 7) week.push(null);
      cols.push(week);
    }
    return cols;
  }, [data]);

  const completedCount = data?.filter(d => d.completed).length || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-[#121212] border border-white/5 rounded-2xl p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <div>
          <h2 className="font-heading font-bold text-white">{t('progress.cards.heatmapTitle')}</h2>
          <p className="text-zinc-500 text-xs">{t('progress.cards.heatmapSubtitle')}</p>
        </div>
        <div className="text-zinc-400 text-sm font-mono">
          <span className="text-amber font-bold">{completedCount}</span> / 90 {t('progress.cards.heatmapDays')}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-fit">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-1">
              {week.map((cell, ci) => (
                <div
                  key={ci}
                  title={cell ? `${cell.date}${cell.completed ? ' ·' : ''}` : ''}
                  className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-sm ${
                    !cell
                      ? 'bg-transparent'
                      : cell.completed
                        ? 'bg-amber'
                        : 'bg-white/5'
                  }`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4 text-xs text-zinc-500">
        <span>{t('progress.cards.heatmapLegendLess')}</span>
        <div className="flex gap-0.5">
          <div className="w-3 h-3 rounded-sm bg-white/5" />
          <div className="w-3 h-3 rounded-sm bg-amber/40" />
          <div className="w-3 h-3 rounded-sm bg-amber/70" />
          <div className="w-3 h-3 rounded-sm bg-amber" />
        </div>
        <span>{t('progress.cards.heatmapLegendMore')}</span>
      </div>
    </motion.div>
  );
};

const RepertoireCard = ({ data, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.15 }}
    className="bg-[#121212] border border-white/5 rounded-2xl p-5 sm:p-6"
  >
    <div className="flex items-baseline justify-between mb-4">
      <div>
        <h2 className="font-heading font-bold text-white">{t('progress.cards.repertoireTitle')}</h2>
        <p className="text-zinc-500 text-xs">{t('progress.cards.repertoireSubtitle')}</p>
      </div>
      <Link to="/songs" className="text-amber text-sm hover:underline">
        {t('progress.cards.openLibrary')} →
      </Link>
    </div>

    {data && data.length > 0 ? (
      <div className="space-y-2">
        {data.map(song => (
          <Link
            key={song.song_id}
            to="/songs"
            className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/10 rounded-xl hover:border-amber/30 transition-colors"
          >
            <Star className={`w-4 h-4 ${song.status === 'completed' ? 'text-amber fill-amber' : 'text-zinc-500'}`} />
            <div className="flex-1 min-w-0">
              <div className="text-zinc-200 text-sm font-semibold truncate">{song.title}</div>
              <div className="text-zinc-500 text-xs">{song.key} · {song.difficulty}</div>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
              song.status === 'completed'
                ? 'bg-green-500/20 text-green-300 border-green-500/30'
                : 'bg-amber/15 text-amber border-amber/30'
            }`}>
              {t(`progress.cards.repertoireStatus.${song.status}`)}
            </span>
          </Link>
        ))}
      </div>
    ) : (
      <div className="text-center text-zinc-500 py-12 text-sm">
        <Music className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
        {t('progress.cards.repertoireEmpty')}
      </div>
    )}
  </motion.div>
);

export default ProgressPage;
